import classNames from "classnames";
import React, { forwardRef, useImperativeHandle, useRef } from "react";

import { BottomMenuPanel } from "../../bottom-menu-panel";
import { BottomMenuButtons, IBottomMenuButtonsRef } from "../../bottom-menu/buttons";
import { TransparentButton } from "../../transparent-button";

import "./color-bottom-menu-panel.css";

export interface IColorBottomMenuPanelRef {
	scrollIntoView(colorCodeToScrollTo: string, scrollAnimated?: boolean): void;
}

/** One button of the TextColorPanel */
const ColorBottomMenuPanelItem = ({
	color,
	isActive,
	onClick,
}: {
	color: eXpress.core.IColor;
	isActive?: boolean;
	onClick(): void;
}) => (
	<TransparentButton // Used transparent button to have button behavior: focusable, cursor
		className={`color-bottom-menu-panel__item ${
			isActive ? "color-bottom-menu-panel__item--active" : ""
		}`}
		onClick={onClick}
	>
		<div
			className="color-bottom-menu-panel__item-color"
			style={{
				backgroundColor: color.hexColorString,
			}}
		>
			{color.isFluo && (
				<span className="color-bottom-menu-panel__item-color--fluo">FLUO</span>
			)}
		</div>
	</TransparentButton>
);
ColorBottomMenuPanelItem.displayName = "ColorBottomMenuPanelItem";

interface IColorBottomMenuPanelProps {
	colors: readonly eXpress.core.IColor[];
	selectedColor: eXpress.HexColorString | undefined;
	onClick?(color: eXpress.core.IColor): void;
	className?: string;
}

/** Panel with a list of supported colors */
export const ColorBottomMenuPanel = forwardRef<
	IColorBottomMenuPanelRef,
	IColorBottomMenuPanelProps
>(({ colors, selectedColor, onClick, className }, parentRef) => {
	const bottomMenuRef = useRef<IBottomMenuButtonsRef>(null);

	// used useImperativeHandle, because when using useEffect with as dependency selectedTextColor,
	// it doesn't scroll if the new zone has the same font family
	// So with useImperativeHandle we can give control to the parent, to scrollIntoView if the selected zone changes
	useImperativeHandle(
		parentRef,
		() => ({
			scrollIntoView: (colorCodeToScrollTo, animate = true) => {
				if (!bottomMenuRef.current) return;
				const index = colorCodeToScrollTo
					? colors.findIndex(
							(color) =>
								eXpress.HexColorString.fromString(colorCodeToScrollTo) ===
								color.hexColorString,
					  )
					: -1;
				bottomMenuRef.current.scrollIntoView(index, {
					animate,
				});
			},
		}),
		[colors],
	);

	return (
		<BottomMenuPanel className={classNames("color-panel", className)}>
			<BottomMenuButtons ref={bottomMenuRef}>
				{colors.map((color) => (
					<ColorBottomMenuPanelItem
						key={color.hexColorString}
						color={color}
						isActive={selectedColor === color.hexColorString}
						onClick={() => onClick?.(color)}
					/>
				))}
			</BottomMenuButtons>
		</BottomMenuPanel>
	);
});
ColorBottomMenuPanel.displayName = "ColorBottomMenuPanel";
